import React, { useState, useEffect } from "react";
import { UserCircleIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";

export default function PartenaireForm({ partenaire, open, onClose, onSave }) {
  const [formData, setFormData] = useState({
    _id: undefined,
    nom_societe: "",
    adresse: "",
    type_structure: "",
    classe_actifs: "",
    nom_referent: "",
    email: "",
    telephone: "",
    logo: "",
    numeroEnregistrement: "",  
    description: "",         
    siteWeb: "",              
    fonctionContact: "",      
    documentsRequis: [],
    statutConformite: "",      
    commentairesInternes: "",  
    dateAjout: new Date().toISOString(),
    volumeAffaires: 0,
    archived: false
  });  

  // Remplit le formulaire en cas de modification d'un partenaire
  useEffect(() => {
    if (partenaire) {
      console.log("Chargement du partenaire dans le formulaire :", partenaire)
      setFormData({
        _id: partenaire._id || undefined,
        nom_societe: partenaire.nom_societe || "",
        adresse: partenaire.adresse || "",
        type_structure: partenaire.type_structure || "",
        classe_actifs: partenaire.classe_actifs || "",
        nom_referent: partenaire.nom_referent || "",
        email: partenaire.email || "",
        telephone: partenaire.telephone || "",
        logo: partenaire.logo || "",
        numeroEnregistrement: partenaire.numeroEnregistrement || "",
        description: partenaire.description || "",
        siteWeb: partenaire.siteWeb || "",
        fonctionContact: partenaire.fonctionContact || "",
        documentsRequis: partenaire.documentsRequis || [],
        statutConformite: partenaire.statutConformite || "",
        commentairesInternes: partenaire.commentairesInternes || "",
        dateAjout: partenaire.dateAjout || new Date().toISOString(),
        volumeAffaires: partenaire.volumeAffaires || 0,
        archived: partenaire.archived ?? false
      });
    }
  }, [partenaire]);  

  // Gestion des changements de champs
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();

    const cleanedData = { ...formData };

    // ✅ Nettoyage des champs vides
    Object.keys(cleanedData).forEach((key) => {
        if (cleanedData[key] === "" || cleanedData[key] === null) {
            cleanedData[key] = undefined;
        }
    });

    // ✅ Vérifier que `_id` est bien géré
    if (!cleanedData._id) {
        delete cleanedData._id; // Supprime `_id` s'il est vide pour éviter une erreur
    }

    console.log("🚀 Formulaire soumis avec nettoyage :", cleanedData);
    onSave(cleanedData);
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-[999]">
      <DialogBackdrop className="fixed inset-0 bg-black/50" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="relative bg-white p-6 rounded-lg shadow-lg w-[650px] max-h-[90vh] overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>

          <DialogTitle className="text-xl font-bold mb-4 text-center">
            {partenaire ? "Modifier le partenaire" : "Ajouter un partenaire"}
          </DialogTitle>

          {/* Upload du logo */}
          <div className="flex flex-col items-center">
            {formData.logo ? (
              <img
                src={formData.logo}
                alt="Logo"
                className="h-20 w-20 object-contain border rounded-lg"
              />
            ) : (
              <UserCircleIcon className="h-20 w-20 text-gray-400" /> 
            )}
            <input
              type="text"
              name="logo"
              placeholder="Lien du logo (URL)"
              className="w-full p-2 border rounded mt-2"
              value={formData.logo}
              onChange={handleChange}
            />
          </div>

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4 mt-4">
            {/* Colonne 1 */}
            <div className="space-y-3">
              <input
                type="text"
                name="nom_societe"
                placeholder="Nom du partenaire *"
                className="w-full p-2 border rounded"
                value={formData.nom_societe}
                onChange={handleChange}
                required
              />

              <select
                name="type_structure"
                className="w-full p-2 border rounded"
                value={formData.type_structure}
                onChange={handleChange}
                required
              >
                <option value="">Type de structure *</option>
                <option value="GP">Gestion de Portefeuille</option>
                <option value="PSAN">Prestataire de Services sur Actifs Numériques</option>
                <option value="Banque">Banque</option>
                <option value="Assurance">Assurance</option>
                <option value="Broker">Broker</option>
                <option value="Autre">Autre</option>
              </select>

              <select
                name="classe_actifs"
                className="w-full p-2 border rounded"
                value={formData.classe_actifs}
                onChange={handleChange}
                required
              >
                <option value="">Classe d'actifs *</option>
                <option value="Fonds Commun de Placement">Fonds Commun de Placement</option>
                <option value="Fonds d'Investissement Alternatif">Fonds d'Investissement Alternatif</option>
                <option value="Cryptomonnaies">Cryptomonnaies</option>
                <option value="Produits Structurés">Produits Structurés</option>
                <option value="Private Equity">Private Equity</option>
                <option value="Produits Immobiliers">Produits Immobiliers</option>
                <option value="Gestion Sous Mandat">Gestion Sous Mandat</option>
                <option value="BUFFER ETF">BUFFER ETF</option>
                <option value="Dette Privée">Dette Privée</option>
                <option value="Investissement Alternatif">Investissements Alternatifs</option>
                <option value="Services Exclusifs">Services Exclusifs</option>
                <option value="Assurance-Vie LUX">Assurance-Vie LUX</option>
              </select>

              <input
                type="text"
                name="nom_referent"
                placeholder="Nom du référent *"
                className="w-full p-2 border rounded"
                value={formData.nom_referent}
                onChange={handleChange}
                required
              />

              <input
                type="email"
                name="email"
                placeholder="Email *"
                className="w-full p-2 border rounded"
                value={formData.email}
                onChange={handleChange}
                required
              />

              <input
                type="text"
                name="telephone"
                placeholder="Téléphone"
                className="w-full p-2 border rounded"
                value={formData.telephone}
                onChange={handleChange}
              />

              {/* Nouveau : Statut conformité */}
              <select
                name="statutConformite"
                className="w-full p-2 border rounded"
                value={formData.statutConformite}
                onChange={handleChange}
              >
                <option value="">Statut du Partenariat</option>
                <option value="Actif">Actif</option>
                <option value="Inactif">Inactif</option>
                <option value="Onboarding">Onboarding</option>
              </select>

            </div>

            {/* Colonne 2 */}
            <div className="space-y-3">
              <input
                type="text"
                name="adresse"
                placeholder="Adresse *"
                className="w-full p-2 border rounded"
                value={formData.adresse}
                onChange={handleChange}
                required
              />

              <input
                type="text"
                name="siteWeb"
                placeholder="Site Web"
                className="w-full p-2 border rounded"
                value={formData.siteWeb}
                onChange={handleChange}
              />

              <input
                type="text"
                name="numeroEnregistrement"
                placeholder="Numéro d'enregistrement"
                className="w-full p-2 border rounded"
                value={formData.numeroEnregistrement}
                onChange={handleChange}
              />

              <textarea
                name="description"
                placeholder="Description"
                className="w-full p-2 border rounded"
                value={formData.description}
                onChange={handleChange}
              />

              <textarea
                name="commentairesInternes"
                placeholder="Commentaires internes"
                className="w-full p-2 border rounded"
                value={formData.commentairesInternes}
                onChange={handleChange}
              />

              {/* Nouveau : Volume d'affaires */}
              <input
                type="number"
                name="volumeAffaires"
                placeholder="Volume d'affaires (€)"
                className="w-full p-2 border rounded"
                value={formData.volumeAffaires}
                onChange={handleChange}
              />

              {/* Nouveau : Documents requis */}
              <textarea
                name="documentsRequis"
                placeholder="Documents requis"
                className="w-full p-2 border rounded"
                value={formData.documentsRequis.join(", ")}
                onChange={(e) => setFormData({ ...formData, documentsRequis: e.target.value.split(", ") })}
              />

              {/* Date d'ajout (lecture seule) */}
              <input
                type="text"
                name="dateAjout"
                className="w-full p-2 border rounded bg-gray-100"
                value={new Date(formData.dateAjout).toLocaleDateString("fr-FR")}
                disabled
              />
            </div>
          </form>

          {/* Boutons Annuler et Ajouter */}
          <div className="flex justify-end space-x-2 mt-4">
            <button 
              type="button" 
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-700"
              onClick={onClose}
            >
              Annuler
            </button>

            <button 
              type="button" 
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700"
              onClick={handleSubmit}
            >
              {partenaire ? "Modifier" : "Ajouter"}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
